import React, { useCallback } from 'react';

import OfferRedemptionModal from 'components/offer-redemption-modal';
import { OffersEmptyState } from 'components/offers-homepage-tims/offers-empty-state';
import { ResponsiveCarousel } from 'components/responsive-carousel';
import useEffectOnceOnCondition from 'hooks/use-effect-once-on-condition';
import useOffersRefresh from 'hooks/use-offer-refresh';
import { useOffersModal } from 'hooks/use-offers-modal';
import { useOffersContext } from 'state/offers';
import { EventName, emitEvent } from 'utils/event-hub';
import { getUniqIdForOffer } from 'utils/offers';

import Offer from './offer';
import { OfferSkeleton } from './offer-skeleton';
import { GridOffersWrapper, OffersList } from './styled';

const MOBILE_SKELETON_NUMBER = 2;
export const DESKTOP_SKELETON_NUMBER = 4;

const getSkeletons = ({ number = MOBILE_SKELETON_NUMBER, isGridOfferLayout = false }) =>
  Array.from({ length: number }, (_, i) => (
    <OfferSkeleton key={`offer_skeleton_${i}`} isGridLayout={isGridOfferLayout} />
  ));

const Offers: React.FC<{ className?: string; isGridOfferLayout?: boolean }> = ({
  className,
  isGridOfferLayout,
}) => {
  const offersCtx = useOffersContext();

  const { modalIsOpen, openModal, closeModal } = useOffersModal(offersCtx);
  const { offers: filteredOffers, loading, offersFeedback, selectOfferById } = offersCtx;

  useOffersRefresh();

  const shouldShowEmptyState = !loading && !filteredOffers?.length;
  const shouldShowOffersInCarousel = Boolean(
    !shouldShowEmptyState && !isGridOfferLayout && !loading
  );

  useEffectOnceOnCondition(
    () => {
      emitEvent(EventName.OFFERS_CAROUSEL_RENDER);
    },
    () => shouldShowOffersInCarousel
  );

  const makeHandleOfferClick = useCallback(
    (id: string) => () => {
      selectOfferById(id);
      openModal();
    },
    [selectOfferById, openModal]
  );

  // Create the array of displayed offer components
  const offersList = filteredOffers.map((offer, i) => {
    const offerId = getUniqIdForOffer({ id: offer._id, token: offer.tokenId });
    const handleOfferClick = makeHandleOfferClick(offerId);
    return (
      <Offer
        key={`${offerId}_${i}`}
        offer={offer}
        offerFeedback={(offersFeedback || {})[offerId]}
        onClick={handleOfferClick}
        isGridLayout={isGridOfferLayout}
      />
    );
  });

  // If the user has no offers, show an empty state
  if (!loading && !filteredOffers?.length) {
    return <OffersEmptyState />;
  }

  // Show skeleton or offers depending on the loading state
  const content = loading
    ? getSkeletons({
        number: isGridOfferLayout ? DESKTOP_SKELETON_NUMBER : MOBILE_SKELETON_NUMBER,
        isGridOfferLayout,
      })
    : offersList;

  return (
    <div data-testid="offers-list">
      {isGridOfferLayout ? (
        <GridOffersWrapper data-testid="grid-offers">{content}</GridOffersWrapper>
      ) : (
        <OffersList className={className}>
          <ResponsiveCarousel alignWithParent>{content}</ResponsiveCarousel>
        </OffersList>
      )}
      <OfferRedemptionModal modalIsOpen={modalIsOpen} closeModal={closeModal} />
    </div>
  );
};

export default Offers;
